import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Slider } from "primereact/slider";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { format } from "date-fns";
import axiosInstance from "../services/axiosInstance";
import Lottie from "lottie-react";
import fireworks_display from "./summeryopen.json";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
    PieChart, Pie, Cell, Legend,
    BarChart, Bar
} from "recharts";

const COLORS = ["#16a34a", "#2563eb", "#f59e0b", "#dc2626", "#8b5cf6", "#1bfd6e", "#bae912", "#f5740b", "#eeb3b3", "#5cecf6"];

const SummaryDialog = ({ visible, onHide, departmentId }) => {
    const [loading, setLoading] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [dateRange, setDateRange] = useState([0, 1]);
    const [debouncedDateRange, setDebouncedDateRange] = useState(dateRange);
    const [showFireworks, setShowFireworks] = useState(false);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedDateRange(dateRange);
        }, 500);
        return () => clearTimeout(handler);
    }, [dateRange]);

    useEffect(() => {
        if (visible) fetchSummaryData();
    }, [visible, debouncedDateRange]);

    useEffect(() => {
        if (visible) {
            setTimeout(() => setShowFireworks(true), 500);
            setTimeout(() => setShowFireworks(false), 2500);
        }
    }, [visible]);

    const fetchSummaryData = async () => {
        setLoading(true);
        try {
            const startDate = format(
                new Date(Date.now() - dateRange[1] * 24 * 60 * 60 * 1000),
                "yyyy-MM-dd"
            );
            const endDate = format(
                new Date(Date.now() - dateRange[0] * 24 * 60 * 60 * 1000),
                "yyyy-MM-dd"
            );

            const response = await axiosInstance.get(
                `/api/summary-stats/?start_date=${startDate}&end_date=${endDate}&top_x=10&department=${departmentId}`
            );
            setSummaryData(response.data);
        } catch (error) {
            console.error("Error fetching summary data:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "LKR",
        }).format(value || 0);

    const paymentBreakdown = summaryData
        ? [
              {
                  name: "Cash",
                  value:
                      summaryData.total_payments.find((p) => p.pay_cash === true)
                          ?.total_received || 0,
              },
              {
                  name: "Non-Cash",
                  value:
                      summaryData.total_payments.find((p) => p.pay_cash === false)
                          ?.total_received || 0,
              },
          ]
        : [];
    
const bestSelling = summaryData?.best_selling_products || [];
const worstSelling = summaryData?.worst_selling_products || [];

// Combine unique product names in order: best first, then worst not already included
const allProducts = [
  ...bestSelling.map(p => p.product__name),
  ...worstSelling
    .map(p => p.product__name)
    .filter(name => !bestSelling.some(b => b.product__name === name))
];

// Build aligned datasets
const bestMap = Object.fromEntries(
  bestSelling.map(p => [p.product__name, p.total_sold])
);
const worstMap = Object.fromEntries(
  worstSelling.map(p => [p.product__name, p.total_sold])
);

const bestWorstChartData = {
  labels: allProducts,
  datasets: [
    {
      label: "Best Selling",
      data: allProducts.map(name => bestMap[name] ?? null),
      backgroundColor: (ctx) => {
        const chart = ctx.chart;
        const { ctx: c, chartArea } = chart;
        if (!chartArea) return "#22c55e";
        const g = c.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        g.addColorStop(0, "#22c55e");
        g.addColorStop(1, "#15803d");
        return g;
      }
    },
    {
      label: "Worst Selling",
      data: allProducts.map(name => worstMap[name] ?? null),
      backgroundColor: (ctx) => {
        const chart = ctx.chart;
        const { ctx: c, chartArea } = chart;
        if (!chartArea) return "#ef4444";
        const g = c.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        g.addColorStop(0, "#f87171");
        g.addColorStop(1, "#b91c1c");
        return g;
      }
    }
  ]
};

    const bestWorstOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
            position: "top"
            },
            tooltip: {
            callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}`
            }
            }
        },
        scales: {
            x: {
            ticks: {
                maxRotation: 45,
                minRotation: 45,
                autoSkip: false,
                font: {
                size: 11
                }
            }
            },
            y: {
            beginAtZero: true
            }
        }
    };

    const revenueData = summaryData?.top_revenue_products || [];
    const revenueChartData = {
    labels: revenueData.map(p => p.product__name),
    datasets: [
        {
        data: revenueData.map(p => p.total_revenue),
        backgroundColor: COLORS,
        hoverOffset: 12
        }
    ]
    };

    const revenueChartOptions = {
        plugins: {
            legend: {
                position: "right",
                labels: {
                    boxWidth: 12,
                    padding: 12
            }
            },
            tooltip: {
                callbacks: {
                    label: (ctx) =>
                    `${ctx.label}: ${Number(ctx.raw).toFixed(2)}`
                }
            }
        },
        cutout: "65%",   // slightly larger hole = better balance
        maintainAspectRatio: false
    };


    return (
        <>
            {showFireworks && (
                <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
                    <Lottie animationData={fireworks_display} loop={false} />
                </div>
            )}

            <Dialog
                visible={visible}
                onHide={onHide}
                header="Sales Dashboard"
                modal
                draggable={false}
                maximized={true}
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
            >
                <div className="mb-4">
                    <h5 className="mb-2 font-medium">Select Date Range</h5>
                    <Slider
                        value={dateRange}
                        onChange={(e) => setDateRange(e.value)}
                        range
                        min={0}
                        max={30}
                        step={1}
                        style={{ width: "100%" }}
                    />
                    <p className="mt-2 text-sm text-color-secondary">
                        Showing stats from{" "}
                        <strong>
                            {format(
                                new Date(
                                    Date.now() -
                                        dateRange[1] * 24 * 60 * 60 * 1000
                                ),
                                "MMM dd, yyyy"
                            )}
                        </strong>{" "}
                        to{" "}
                        <strong>
                            {format(
                                new Date(
                                    Date.now() -
                                        dateRange[0] * 24 * 60 * 60 * 1000
                                ),
                                "MMM dd, yyyy"
                            )}
                        </strong>
                    </p>
                </div>

                {loading ? (
                    <div className="flex justify-center py-6">
                        <ProgressSpinner />
                    </div>
                ) : (
                    summaryData && (
                        <div className="p-fluid w-full surface-ground p-3">

                            <div className="flex flex-column gap-4">
                            {/* TOP ROW: 40% cards (left) + 60% sales trend (right) */}
                            <div className="flex flex-row gap-4 align-items-stretch w-full mb-4">

  {/* -------- LEFT: 40% — KPI PANEL -------- */}
  <div 
    style={{ width: "40%" }} 
    className="surface-card p-4 border-round shadow-1"
  >
    <div className="flex flex-column gap-4">

      {/* -------- ROW 1 -------- */}
      <div className="flex flex-row gap-4">

        <Card 
          className="flex-1 shadow-2 border-left-4 border-blue-500"
        >
          <h6 className="text-sm text-color-secondary mb-1">Total Sales</h6>
          <p className="text-2xl font-bold text-primary">
            {summaryData.total_sales} units
          </p>
        </Card>

        <Card className="flex-1 shadow-2 border-left-4 border-green-500">
          <h6 className="text-sm text-color-secondary mb-1">Total Payments</h6>
          <p className="font-medium">
            Cash: {formatCurrency(paymentBreakdown[0].value)}
          </p>
          <p className="font-medium">
            Non-Cash: {formatCurrency(paymentBreakdown[1].value)}
          </p>
        </Card>

      </div>

      {/* -------- ROW 2 -------- */}
      <div className="flex flex-row gap-4">

        <Card className="flex-1 shadow-2 border-left-4 border-yellow-500">
          <h6 className="text-sm text-color-secondary mb-1">New Customers</h6>
          <p className="text-2xl font-bold text-color">
            {summaryData.new_customers}
          </p>
        </Card>

        <Card className="flex-1 shadow-2 border-left-4 border-rose-500">
          <h6 className="text-sm text-color-secondary mb-1">
            Pending Supplier Payments
          </h6>
          <p className="font-medium">
            Invoices: {summaryData.pending_supplier_payments?.count || 0}
          </p>
          <p className="font-medium">
            Due:{" "}
            {formatCurrency(
              summaryData.pending_supplier_payments?.total_due || 0
            )}
          </p>
        </Card>

      </div>
    </div>
  </div>

  {/* -------- RIGHT: 60% — SALES TREND (PRIMARY VISUAL) -------- */}
  <div style={{ width: "60%" }}>
    <Card className="h-full shadow-3 border-round">
      <h6 className="mb-2 font-semibold text-color">
        📈 Sales Trend (Units per Day)
      </h6>

      <ResponsiveContainer width="100%" height={275}>
        <LineChart data={summaryData.daily_sales}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="units"
            stroke="#2563eb"
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  </div>
</div>


                        <div className="flex flex-row gap-4 align-items-stretch w-full mb-4">

                        {/* --- PAYMENT BEHAVIOR (PIE) --- */}
                        <div style={{ width: "40%" }}>
                        <Card className="shadow-3 border-round surface-card">
                            <h6 className="mb-2 font-semibold">💳 Payment Behavior</h6>
                            <ResponsiveContainer width="100%" height={320}>
                                <PieChart>
                                <Pie
                                    data={paymentBreakdown}
                                    dataKey="value"
                                    nameKey="name"
                                    outerRadius={90}
                                    label
                                >
                                    {paymentBreakdown.map((_, i) => (
                                    <Cell key={i} fill={COLORS[i]} />
                                    ))}
                                </Pie>
                                <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Card>
                        </div>
                        {/* ===================== TOP 10 REVENUE ===================== */}
                        <div style={{ width: "60%" }}>
                        <Card className="shadow-3 border-round surface-card h-full">
                            <h6 className="mb-3 font-semibold">
                            🏆 Top 10 Products by Revenue
                            </h6>

                            <div className="flex justify-content-center align-items-center w-full">
                            <Chart
                                type="doughnut"
                                data={revenueChartData}
                                options={revenueChartOptions}
                                style={{ width: "100%", maxWidth: "520px", height: "320px" }}
                            />
                            </div>
                        </Card>
                        </div>

                        </div>

                        {/* ===================== BEST vs WORST SELLING ===================== */}
                        <div style={{ width: "100%" }}>
                        <Card className="shadow-3 border-round surface-card h-full">
                            <h6 className="mb-3 font-semibold">
                            📊 Best vs Worst Selling Products
                            </h6>

                            <div className="flex justify-content-center w-full">
                            <Chart
                                type="bar"
                                data={bestWorstChartData}
                                options={bestWorstOptions}
                                style={{ width: "100%", height: "320px" }}
                            />
                            </div>
                        </Card>
                        </div>


                      {/* ===================== FULL WIDTH ROW: STOCK ALERTS (CENTERED) ===================== */}
                        <div className="flex justify-content-center w-full">
                        <Card className="shadow-3 border-round surface-card" style={{ width: "50%" }}>
                            <h6 className="mb-2 text-center font-semibold">⚠️ Stock Alerts</h6>

                            {summaryData.stock_alerts.length > 0 ? (
                            <DataTable value={summaryData.stock_alerts}>
                                <Column field="product__name" header="Product" />
                                <Column field="quantity" header="Quantity Left" />
                            </DataTable>
                            ) : (
                            <p className="text-center font-medium text-green-700">
                                No stock alerts 🎉
                            </p>
                            )}
                        </Card>
                        </div>
                    </div>
                    </div>


                    )
                )}
            </Dialog>
        </>
    );
};

export default SummaryDialog;
